import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { deleteCompany } from '@/store/slices/companiesSlice';
import type {
  Dictionary,
  IsReadFilter,
  Notification,
} from '@flemingmetrics/api-client';
import { addToIdArray } from '@/utils/arrays';

type NotificationsState = {
  byId: Dictionary<Notification>;
  byCompanyId: Dictionary<string[]>;
};

const initialState = {
  byId: {},
  byCompanyId: {},
} satisfies NotificationsState as NotificationsState;

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      return getNextState(state, [action.payload]);
    },
    setNotifications: (state, action: PayloadAction<Notification[]>) => {
      return getNextState(state, action.payload);
    },
    updateNotification: (
      state,
      action: PayloadAction<{ notificationId: number; isRead: boolean }>
    ) => {
      const key = `${action.payload.notificationId}`;
      if (state.byId[key]) state.byId[key].isRead = action.payload.isRead;
    },
    updateAllNotifications: (
      state,
      action: PayloadAction<{ isRead: boolean }>
    ) => {
      Object.keys(state.byId).forEach((key) => {
        state.byId[key].isRead = action.payload.isRead;
      });
    },
    deleteNotification: (
      state,
      action: PayloadAction<{ notificationId: number }>
    ) => {
      delete state.byId[`${action.payload.notificationId}`];
    },
    deleteAllNotifications: (
      state,
      action: PayloadAction<{ filter?: IsReadFilter }>
    ) => {
      if (action.payload.filter) {
        Object.keys(state.byId).forEach((key) => {
          if (state.byId[key].isRead === action.payload.filter!.value) {
            delete state.byId[key];
          }
        });
      } else {
        state.byId = {};
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deleteCompany, (state, action) => {
      const { companyId } = action.payload;
      const keys = state.byCompanyId[companyId];
      if (!keys) return state;
      keys.forEach((key) => {
        delete state.byId[key];
      });
      delete state.byCompanyId[companyId];
    });
  },
});

function getNextState(
  state: NotificationsState,
  notifications: Notification[]
) {
  let byId = { ...state.byId };
  let byCompanyId = { ...state.byCompanyId };
  notifications.forEach((notification) => {
    const key = `${notification.id}`;
    byId[key] = notification;
    byCompanyId[notification.companyId] = addToIdArray(
      byCompanyId[notification.companyId],
      key
    );
  });
  return { byId, byCompanyId };
}

export const {
  addNotification,
  setNotifications,
  updateAllNotifications,
  deleteNotification,
  deleteAllNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
